import jsPDF from "jspdf";

/**
 * Generate a Danish receipt PDF for a completed job.
 * data: { job, transaction, customer, driver, issued_at }
 */
export function generateReceiptPDF(data, lang = "da") {
  const t = lang === "en" ? EN : DA;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  let y = 20;

  // Header
  doc.setFillColor(56, 109, 84); // mosgrøn
  doc.rect(0, 0, W, 12, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Grabber", 15, 8);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(t.tagline, W - 15, 8, { align: "right" });

  // Title
  doc.setTextColor(20, 20, 20);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  y = 25;
  doc.text(t.receipt, 15, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120, 120, 120);
  doc.text(`${t.issued} ${new Date(data.issued_at).toLocaleDateString(lang === "en" ? "en-GB" : "da-DK")}`, 15, y);
  doc.text(`${t.job_id}: ${data.job.id.slice(0, 8)}`, W - 15, y, { align: "right" });

  // Job info box
  y += 12;
  doc.setDrawColor(220, 220, 220);
  doc.setFillColor(248, 247, 244);
  doc.roundedRect(15, y, W - 30, 30, 2, 2, "FD");
  y += 6;
  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(data.job.title, 19, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(`${t.pickup}: ${data.job.pickup_address}`, 19, y);
  if (data.job.dropoff_address) {
    y += 4.5;
    doc.text(`${t.dropoff}: ${data.job.dropoff_address}`, 19, y);
  }
  y += 4.5;
  doc.text(`${t.status}: ${data.job.status}`, 19, y);

  // Parties
  y += 12;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(t.customer, 19, y);
  doc.text(t.driver, 110, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  if (data.customer) {
    doc.text(data.customer.full_name || "-", 19, y);
    doc.text(data.customer.email || "-", 19, y + 4);
    if (data.customer.phone) doc.text(data.customer.phone, 19, y + 8);
  }
  if (data.driver) {
    doc.text(data.driver.full_name || "-", 110, y);
    doc.text(data.driver.email || "-", 110, y + 4);
    if (data.driver.phone) doc.text(data.driver.phone, 110, y + 8);
  }

  // Amount breakdown
  y += 22;
  doc.setDrawColor(220, 220, 220);
  doc.line(15, y, W - 15, y);
  y += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  doc.text(t.amount_breakdown, 15, y);

  if (data.transaction) {
    const tx = data.transaction;
    y += 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    row(doc, 15, y, t.total_paid, `${tx.full_amount.toFixed(2)} DKK`);
    y += 7;
    row(doc, 15, y, t.driver_share, `${tx.driver_amount.toFixed(2)} DKK`, "(80%)");
    y += 7;
    row(doc, 15, y, t.commission, `${tx.grabber_commission.toFixed(2)} DKK`, "(20%)");
    y += 4;
    doc.setDrawColor(180, 180, 180);
    doc.line(15, y, W - 15, y);
    y += 7;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(56, 109, 84);
    doc.text(t.total, 15, y);
    doc.text(`${tx.full_amount.toFixed(2)} DKK`, W - 15, y, { align: "right" });
    if (tx.status === "refunded") {
      y += 8;
      doc.setTextColor(180, 50, 50);
      doc.setFontSize(11);
      doc.text(`⚠ ${t.refunded}`, 15, y);
    }
  } else {
    y += 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(t.no_transaction, 15, y);
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.setFont("helvetica", "normal");
  doc.text(t.footer, W / 2, 285, { align: "center" });
  doc.text("Grabber.dk · support@grabber.dk", W / 2, 290, { align: "center" });

  doc.save(`grabber-kvittering-${data.job.id.slice(0, 8)}.pdf`);
}

function row(doc, x, y, label, value, sub) {
  doc.setTextColor(80, 80, 80);
  doc.text(label, x, y);
  if (sub) {
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.text(sub, x + 50, y);
    doc.setFontSize(10);
  }
  doc.setTextColor(20, 20, 20);
  doc.text(value, 210 - 15, y, { align: "right" });
}

const DA = {
  tagline: "Peer-to-peer transport i Danmark",
  receipt: "Kvittering",
  issued: "Udstedt:",
  job_id: "Job ID",
  pickup: "Afhentning",
  dropoff: "Aflevering",
  status: "Status",
  customer: "Kunde",
  driver: "Chauffør",
  amount_breakdown: "Beløb",
  total_paid: "Betalt af kunde",
  driver_share: "Til chauffør",
  commission: "Platformskommission",
  total: "Total",
  refunded: "Refunderet",
  no_transaction: "Ingen betalingsinfo tilgængelig",
  footer: "Tak fordi du brugte Grabber",
};

const EN = {
  tagline: "Peer-to-peer transport in Denmark",
  receipt: "Receipt",
  issued: "Issued:",
  job_id: "Job ID",
  pickup: "Pickup",
  dropoff: "Drop-off",
  status: "Status",
  customer: "Customer",
  driver: "Driver",
  amount_breakdown: "Amount",
  total_paid: "Paid by customer",
  driver_share: "To driver",
  commission: "Platform commission",
  total: "Total",
  refunded: "Refunded",
  no_transaction: "No payment info available",
  footer: "Thank you for using Grabber",
};
